import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react"
import { createPortal } from "react-dom"
import { toastBus } from "@/features/toast/toastBus"
import type { ToastInput, ToastItem } from "@/features/toast/types"
import { ToastViewport } from "@/widgets/toast/ToastViewport"

const DEFAULT_DURATION_MS = 5000
const MAX_VISIBLE = 4

type ToastContextValue = {
  push: (input: ToastInput) => string
  dismiss: (id: string) => void
  dismissAll: () => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

const makeId = () => {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID()
  }
  return `toast-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

export const ToastProvider = ({ children }: { children: ReactNode }) => {
  const [toasts, setToasts] = useState<ToastItem[]>([])
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())

  const clearTimer = useCallback((id: string) => {
    const t = timersRef.current.get(id)
    if (t !== undefined) {
      clearTimeout(t)
      timersRef.current.delete(id)
    }
  }, [])

  const dismiss = useCallback(
    (id: string) => {
      clearTimer(id)
      setToasts((prev) => prev.filter((t) => t.id !== id))
    },
    [clearTimer],
  )

  const dismissAll = useCallback(() => {
    for (const id of timersRef.current.keys()) clearTimer(id)
    setToasts([])
  }, [clearTimer])

  const push = useCallback(
    (input: ToastInput) => {
      const id = input.id ?? makeId()
      const duration = input.durationMs ?? DEFAULT_DURATION_MS
      const item: ToastItem = {
        id,
        severity: input.severity,
        title: input.title,
        description: input.description,
        durationMs: duration,
        onClick: input.onClick,
      }

      setToasts((prev) => {
        const withoutDup = prev.filter((t) => t.id !== id)
        const next = [...withoutDup, item]
        /** Если очередь переполнена — выбрасываем самые старые. */
        while (next.length > MAX_VISIBLE) {
          const removed = next.shift()
          if (removed) clearTimer(removed.id)
        }
        return next
      })

      if (duration > 0) {
        const timer = setTimeout(() => dismiss(id), duration)
        timersRef.current.set(id, timer)
      }
      return id
    },
    [clearTimer, dismiss],
  )

  useEffect(() => {
    return toastBus.subscribe((input) => {
      push(input)
    })
  }, [push])

  useEffect(() => {
    const timersMap = timersRef.current
    return () => {
      for (const timer of timersMap.values()) clearTimeout(timer)
      timersMap.clear()
    }
  }, [])

  const value = useMemo(() => ({ push, dismiss, dismissAll }), [push, dismiss, dismissAll])

  const portalTarget = typeof document !== "undefined" ? document.body : null

  return (
    <ToastContext.Provider value={value}>
      {children}
      {portalTarget ? createPortal(<ToastViewport toasts={toasts} onDismiss={dismiss} />, portalTarget) : null}
    </ToastContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components -- useToast is a hook, not a component
export const useToast = () => {
  const ctx = useContext(ToastContext)
  if (!ctx) {
    throw new Error("useToast must be used within ToastProvider")
  }
  return ctx
}
